<template>
  <div id="app">
    <div class="container mx-auto p-4">
      <h1 class="mb-2 mt-8">Vue Renderless Carousel</h1>
      <h2 class="mb-4">Sample implementations</h2>
      <p class="mb-8">
        Here are some examples of the sort of things you can do on top of
        <a
          href="https://github.com/mpbarlow/vue-renderless-carousel"
          target="_blank"
        >vue-renderless-carousel</a>. Please feel free to use these, or base your own carousels off them. CSS utility classes
        via the fantastic
        <a
          href="https://tailwindcss.com"
        >Tailwind CSS</a>.
      </p>
      <div class="mb-8">
        <h3>Table of Contents</h3>
        <ul>
          <li>
            <a href="#bar">Bar</a>
          </li>
          <li>
            <a href="#vertical">Vertical</a>
          </li>
          <li>
            <a href="#number">Number</a>
          </li>
          <li>
            <a href="#arrow">Arrow</a>
          </li>
          <li>
            <a href="#synchronised">Synchronised</a>
          </li>
          <li>
            <a href="#dynamic">Dynamic</a>
          </li>
          <li>
            <a href="#alignable">Different Alignments</a>
          </li>
        </ul>
      </div>
      <div class="p-4">
        <a name="bar"></a>
        <h3 class="mb-4">Bar</h3>
        <ul>
          <li>Bar controls underneath</li>
          <li>Variable size slides</li>
          <li>Autoscrolling via interval function</li>
        </ul>
      </div>
      <div class="my-8">
        <bar-carousel>
          <rl-carousel-slide>
            <div class="border border-blue p-8 mx-8 text-center text-3xl" style="width: 20vw;">One</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div class="border border-red p-8 mx-8 text-center text-3xl" style="width: 40vw;">Two</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div
              class="border border-green p-8 mx-8 text-center text-3xl"
              style="width: 80vw;"
            >Three</div>
          </rl-carousel-slide>
        </bar-carousel>
        <div class="text-center mt-8">
          <button @click="showBar = !showBar" class="font-semibold mb-2 mt-8">
            {{ showBar ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showBar">
          <code data-gist-id="a96f139e8151bc1d00ca60ad1259f986"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="vertical"></a>
        <h3 class="mb-4">Vertical</h3>
        <ul>
          <li>Circular controls on the left</li>
          <li>Variable size slides</li>
        </ul>
      </div>
      <div class="m-8">
        <vertical-carousel>
          <rl-carousel-slide>
            <div
              class="border border-blue p-8 ml-4 mr-8 my-8 text-3xl flex items-center justify-center"
              style="height: 50px;"
            >One</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div
              class="border border-red p-8 ml-4 mr-8 my-4 text-3xl flex items-center justify-center"
              style="height: 100px;"
            >Two</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div
              class="border border-green p-8 ml-4 mr-8 my-4 text-3xl flex items-center justify-center"
              style="height: 150px;"
            >Three</div>
          </rl-carousel-slide>
        </vertical-carousel>
        <div class="text-center mt-8">
          <button @click="showVertical = !showVertical" class="font-semibold mb-2 mt-8">
            {{ showVertical ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showVertical">
          <code data-gist-id="bdfa626cf08b8f243cf5b77dbf40c7dd"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="number"></a>
        <h3 class="mb-4">Number</h3>
        <ul>
          <li>Numeric controls overlapping content</li>
        </ul>
      </div>
      <div class="my-8">
        <number-carousel>
          <rl-carousel-slide class="w-full">
            <div
              class="flex items-center justify-center text-3xl border border-blue bg-blue-lightest p-8 m-8"
            >One</div>
          </rl-carousel-slide>
          <rl-carousel-slide class="w-full">
            <div
              class="flex items-center justify-center text-3xl border border-red p-8 bg-red-lightest m-8"
            >Two</div>
          </rl-carousel-slide>
          <rl-carousel-slide class="w-full">
            <div
              class="flex items-center justify-center text-3xl border border-green p-8 bg-green-lightest m-8"
            >Three</div>
          </rl-carousel-slide>
        </number-carousel>
        <div class="text-center mt-8">
          <button @click="showNumber = !showNumber" class="font-semibold mb-2 mt-8">
            {{ showNumber ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showNumber">
          <code data-gist-id="f57f264bd0ae5b755ec6b7ccd2d3e43f"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="arrow"></a>
        <h3 class="mb-4">Arrow</h3>
        <ul>
          <li>No wrapping</li>
          <li>Animated chevron controls</li>
          <li>No animation on transition</li>
        </ul>
      </div>
      <div class="my-8">
        <arrow-carousel>
          <rl-carousel-slide class="w-full">
            <div class="border border-blue p-8 mx-8 mt-8 text-center text-3xl">One</div>
          </rl-carousel-slide>
          <rl-carousel-slide class="w-full">
            <div class="border border-red p-8 mx-8 mt-8 text-center text-3xl">Two</div>
          </rl-carousel-slide>
          <rl-carousel-slide class="w-full">
            <div class="border border-green p-8 mx-8 mt-8 text-center text-3xl">Three</div>
          </rl-carousel-slide>
          <rl-carousel-slide class="w-full">
            <div class="border border-orange p-8 mx-8 mt-8 text-center text-3xl">Four</div>
          </rl-carousel-slide>
        </arrow-carousel>
        <div class="text-center mt-8">
          <button @click="showArrow = !showArrow" class="font-semibold mb-2 mt-8">
            {{ showArrow ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showArrow">
          <code data-gist-id="dd036ff721115075d77a38ce363f88df"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="synchronised"></a>
        <h3 class="mb-4">Synchronised</h3>
        <ul>
          <li>Changing one immediately updates the other</li>
          <li>Driven by events emitted by each carousel</li>
        </ul>
      </div>
      <div class="my-8">
        <synchronized-carousel>
          <template slot="first">
            <rl-carousel-slide>
              <div
                class="border border-blue p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 20vw;"
              >One</div>
            </rl-carousel-slide>
            <rl-carousel-slide>
              <div
                class="border border-red p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 20vw;"
              >Two</div>
            </rl-carousel-slide>
            <rl-carousel-slide>
              <div
                class="border border-green p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 20vw;"
              >Three</div>
            </rl-carousel-slide>
          </template>
          <template slot="second">
            <rl-carousel-slide>
              <div
                class="border border-blue p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 40vw;"
              >One</div>
            </rl-carousel-slide>
            <rl-carousel-slide>
              <div
                class="border border-red p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 40vw;"
              >Two</div>
            </rl-carousel-slide>
            <rl-carousel-slide>
              <div
                class="border border-green p-8 mx-8 mt-8 text-center text-3xl"
                style="width: 40vw;"
              >Three</div>
            </rl-carousel-slide>
          </template>
        </synchronized-carousel>
        <div class="text-center mt-8">
          <button @click="showSynchronized = !showSynchronized" class="font-semibold mb-2 mt-8">
            {{ showSynchronized ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showSynchronized">
          <code data-gist-id="ccaefc4fb1071ec6319cdbd668939c94"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="dynamic"></a>
        <h3 class="mb-4">Dynamic</h3>
        <ul>
          <li>Variable number of slides</li>
        </ul>
      </div>
      <div class="my-8">
        <bar-carousel>
          <rl-carousel-slide v-for="slide in dynamicSlides" :key="slide">
            <div
              class="border border-blue p-8 mx-8 text-center text-3xl"
              style="width: 20vw;"
            >{{ slide }}</div>
          </rl-carousel-slide>
        </bar-carousel>
        <div class="flex justify-around">
          <button @click="dynamicSlides += 1">Add Slide</button>
          <button @click="dynamicSlides -= 1" :disabled="dynamicSlides <= 1">Remove Slide</button>
        </div>
        <div class="text-center mt-8">
          <button @click="showDynamic = !showDynamic" class="font-semibold mb-2 mt-8">
            {{ showDynamic ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showDynamic">
          <code data-gist-id="3270a28bea0f8d6e6aff33f33af739a6"></code>
        </div>
      </div>
      <hr>
      <div class="p-4">
        <a name="alignable"></a>
        <h3 class="mb-4">Different Alignments</h3>
        <ul>
          <li>Align the active slide either left, center, or right</li>
        </ul>
      </div>
      <div class="my-8">
        <alignable-carousel :align="dynamicAlign">
          <rl-carousel-slide>
            <div class="border border-blue p-8 mx-8 text-center text-3xl" style="width: 20vw;">One</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div class="border border-red p-8 mx-8 text-center text-3xl" style="width: 40vw;">Two</div>
          </rl-carousel-slide>
          <rl-carousel-slide>
            <div
              class="border border-green p-8 mx-8 text-center text-3xl"
              style="width: 80vw;"
            >Three</div>
          </rl-carousel-slide>
        </alignable-carousel>
        <div class="flex justify-around">
          <button @click="dynamicAlign = 'left'">Left</button>
          <button @click="dynamicAlign = 'center'">Centre</button>
          <button @click="dynamicAlign = 'right'">Right</button>
        </div>
        <div class="text-center mt-8">
          <button @click="showBar = !showBar" class="font-semibold mb-2 mt-8">
            {{ showBar ? 'Hide' : 'Show' }} Code
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        <div v-show="showBar">
          <code data-gist-id="37c8f4825100ee56961b280795f2c001"></code>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { RlCarouselSlide } from 'vue-renderless-carousel'

import AlignableCarousel from './components/AlignableCarousel.vue'
import ArrowCarousel from './components/ArrowCarousel.vue'
import BarCarousel from './components/BarCarousel.vue'
import NumberCarousel from './components/NumberCarousel.vue'
import VerticalCarousel from './components/VerticalCarousel.vue'
import SynchronizedCarousel from './components/SynchronizedCarousel.vue'

import './less/index.less'

export default {
  name: 'app',
  components: {
    AlignableCarousel,
    ArrowCarousel,
    NumberCarousel,
    RlCarouselSlide,
    BarCarousel,
    SynchronizedCarousel,
    VerticalCarousel
  },
  data() {
    return {
      showArrow: false,
      showBar: false,
      showDynamic: false,
      showNumber: false,
      showVertical: false,
      showSynchronized: false,
      dynamicSlides: 3,
      dynamicAlign: 'center'
    }
  }
}
</script>
